import { Component, OnInit, OnDestroy, Inject, ViewChild } from '@angular/core';

import { AngularFirestore, AngularFirestoreCollection } from '@angular/fire/compat/firestore';
import { Observable, Subscription } from 'rxjs';

import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';

import { ConfigurationService } from '../../configuration.service';
import { ProjectLanguage } from '../../firestore-models/project-language';
import { Floor } from '../../firestore-models/floor';
import { ApiFloor } from '../../api-client-module';

import { AppConst } from '../../app-const';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-select-entrance-exit-event-floors-dialog',
  templateUrl: './select-entrance-exit-event-floors-dialog.component.html',
  styleUrls: ['./select-entrance-exit-event-floors-dialog.component.css'],
  standalone: false,
})
export class SelectEntranceExitEventFloorsDialogComponent implements OnInit {

  projectLanguages!: Array<ProjectLanguage>;

  private floorsCollection!: AngularFirestoreCollection<Floor>;
  floorsObservable!: Observable<Floor[]>;
  floorsSubscribe!: Subscription;
  projectId: string;

  displayedColumns: string[] = ['position', 'select', 'floor_id', 'explain', 'hall', 'floor'];
  dataSource = new MatTableDataSource<Floor>();
  selection = new SelectionModel<Floor>(true, []);

  hallsCollectionName = AppConst.FIRESTORE_HALLS;

  projectBaseLang: string = 'ja';

  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor(
    public dialogRef: MatDialogRef<SelectEntranceExitEventFloorsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ApiFloor,
    private angularFirestore: AngularFirestore,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
      this.projectId = this.configurationService.getProjectId();
    }

  ngOnInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.floorsCollection = this.angularFirestore.collection<Floor>(
      AppConst.FIRESTORE_FLOORS, ref => ref.where('project_id', '==', this.projectId).orderBy('floor_id', 'asc'));
    this.floorsObservable = this.floorsCollection.valueChanges({ idField: 'id' });
    this.floorsSubscribe = this.floorsObservable.subscribe(floors => {
      this.dataSource.data = this.addFilterValue(floors);
    });
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
  }

  ngOnDestroy(): void {
    this.floorsSubscribe.unsubscribe();
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle() {
    this.isAllSelected() ?
    this.selection.clear() :
    this.dataSource.data.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: Floor): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'}`;
  }

  selectFloors() {
    let floors: Array<ApiFloor> = new Array<ApiFloor>();
    for (const row of this.dataSource.data) {
      if (this.selection.isSelected(row)) {
        const floor: ApiFloor = {
          id: row.id,
          floor_id: row.floor_id,
          floor_explain: row.floor_explain,
          hall_id: row.hall_id,
          floor_names: row.floor_names,
          floor_descs: row.floor_descs,
          // latitude: row.floor_geopoint.latitude,
          // longitude: row.floor_geopoint.longitude,
          floor_file_expand: row.floor_file_expand,
          show_marker: row.show_marker,
          externallink_names: row.externallink_names,
          externallink_urls: row.externallink_urls,
          use_externallink: row.use_externallink,
          exhibit_ids: row.exhibit_ids,
          floor_files: row.floor_files,
          project_id: row.project_id,
          enabled: row.enabled,
        };
        floors.push(floor);
      }
    }
    this.dialogRef.close(floors);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  sortData(sort: Sort) {
    const data = this.dataSource.data.slice();
    if (!sort.active || sort.direction === '') {
      this.dataSource.data = data;
      return;
    }
    const compare = (a: number | string, b: number | string, isAsc: boolean) => {
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    this.dataSource.data = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'floor_id':
          return compare(a.floor_id, b.floor_id, isAsc);
        case 'explain':
          return compare(a.floor_explain, b.floor_explain, isAsc);
        case 'hall':
          return compare(a.hall_id, b.hall_id, isAsc);
        case 'floor':
          const aName = a.floor_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          const bName = b.floor_names.filter((d) => { return d.lang_code == this.projectLanguages[0].lang_code})[0].name;
          return compare(aName, bName, isAsc);
        default:
          return 0;
      }
    });
  }

  addFilterValue(docs: Array<any>) :Array<any> {
    for (const doc of docs) {
      let filterValue = '';
      // for (const value of doc.hall_names) {
      //   filterValue += value.name + ' '
      // }
      for (const value of doc.floor_names) {
        filterValue += value.name + ' '
      }
      filterValue += doc.floor_explain + ' ';
      doc['filterValue'] = filterValue;
    }
    return docs;
  }

  applyFilter(event: Event) {
    this.selection.clear();
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

}
